// Wave 3.45 — Phase 1 — Jewellery Repair Workflow
// =============================================================================
// RepairIntakeDialog
// =============================================================================
// One-stop dialog used by Goldsmith Detail → Repairs section to capture a new
// repair item. Supports two intake paths and two detail modes (per the user's
// confirmed decisions in plan.md § Wave 3.45):
//
//   • Intake type → External (customer property) | Internal (own jewellery)
//   • Detail mode → Gross weight only            | Full breakdown by material
//
// On submit the backend creates the customer_jewellery_items record AND a
// linked Repair Order Form (order_type='repair') numbered RPR-xxx so the
// goldsmith has a proper Order Form to track every subsequent issue/return
// against.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Coins, Gem, Sparkles, Circle, Loader2, Wrench } from 'lucide-react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';

const EMPTY_GOLD_LINE = { purity_id: '', grams: '' };
const EMPTY_CARAT_LINE = { classification_id: '', classification_label: '', carats: '', pieces: '' };

export function RepairIntakeDialog({ open, onOpenChange, goldsmith, companyId, onCreated }) {
  // ------------------------------------------------------------------ form state
  const [intakeType, setIntakeType] = useState('external');
  const [detailMode, setDetailMode] = useState('gross');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [intakeNotes, setIntakeNotes] = useState('');
  const [specialComments, setSpecialComments] = useState('');
  const [jewelleryPurityId, setJewelleryPurityId] = useState('');
  const [estimatedCompletion, setEstimatedCompletion] = useState('');

  const [goldLines, setGoldLines] = useState([]);
  const [diamondLines, setDiamondLines] = useState([]);
  const [colorStoneLines, setColorStoneLines] = useState([]);
  const [pearlLines, setPearlLines] = useState([]);

  // master data (purities + classifications)
  const [purities, setPurities] = useState([]);
  const [diamondClass, setDiamondClass] = useState([]);
  const [colorStoneClass, setColorStoneClass] = useState([]);
  const [pearlClass, setPearlClass] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [mastersLoading, setMastersLoading] = useState(false);

  // ---------------------------------------------------------------- effects
  useEffect(() => {
    if (!open) return;
    // reset state on every open
    setIntakeType('external');
    setDetailMode('gross');
    setTitle('');
    setDescription('');
    setCustomerName('');
    setCustomerPhone('');
    setCustomerAddress('');
    setGrossWeight('');
    setIntakeNotes('');
    setSpecialComments('');
    setEstimatedCompletion('');
    setGoldLines([]);
    setDiamondLines([]);
    setColorStoneLines([]);
    setPearlLines([]);
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadMasters = async () => {
    if (!companyId) return;
    setMastersLoading(true);
    try {
      const [pur, dia, cs, pl] = await Promise.all([
        api.get('/gold/purities', { params: { company_id: companyId } }),
        api.get('/diamonds/classifications', { params: { company_id: companyId } }),
        api.get('/color-stones/classifications', { params: { company_id: companyId } }),
        api.get('/pearls/classifications', { params: { company_id: companyId } }),
      ]);
      setPurities(pur.data || []);
      setDiamondClass(dia.data || []);
      setColorStoneClass(cs.data || []);
      setPearlClass(pl.data || []);
      // Default to highest-percent purity for the order form purity
      const defaultPur = (pur.data || []).slice().sort((a, b) => (b.percent || 0) - (a.percent || 0))[0];
      if (defaultPur) setJewelleryPurityId(defaultPur.id);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load master data'));
    } finally {
      setMastersLoading(false);
    }
  };

  // ---------------------------------------------------------------- helpers
  const addGoldLine = () => setGoldLines((l) => [...l, { ...EMPTY_GOLD_LINE }]);
  const removeGoldLine = (i) => setGoldLines((l) => l.filter((_, idx) => idx !== i));
  const updateGoldLine = (i, patch) =>
    setGoldLines((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const addCaratLine = (setter) => setter((l) => [...l, { ...EMPTY_CARAT_LINE }]);
  const removeCaratLine = (setter, i) => setter((l) => l.filter((_, idx) => idx !== i));
  const updateCaratLine = (setter, i, patch) =>
    setter((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const isDetailed = detailMode === 'detailed';
  const isExternal = intakeType === 'external';

  // ---------------------------------------------------------------- validation
  const validation = useMemo(() => {
    if (!title.trim()) return 'Title is required';
    if (!goldsmith?.id) return 'Goldsmith is missing';
    if (detailMode === 'gross') {
      const gw = parseFloat(grossWeight);
      if (!gw || gw <= 0) return 'Incoming gross weight (grams) is required';
    } else {
      const anyLine =
        goldLines.length > 0 ||
        diamondLines.length > 0 ||
        colorStoneLines.length > 0 ||
        pearlLines.length > 0;
      const gw = parseFloat(grossWeight);
      if (!anyLine && !(gw > 0)) {
        return 'Add at least one component line or set the gross weight';
      }
      for (const gl of goldLines) {
        if (!gl.purity_id) return 'Each gold line needs a purity';
        if (!(parseFloat(gl.grams) > 0)) return 'Each gold line needs grams > 0';
      }
      for (const cl of diamondLines) {
        if (!cl.classification_id && !(cl.classification_label || '').trim()) {
          return 'Each diamond line needs a classification';
        }
      }
      for (const cl of colorStoneLines) {
        if (!cl.classification_id && !(cl.classification_label || '').trim()) {
          return 'Each colour-stone line needs a classification';
        }
      }
      for (const cl of pearlLines) {
        if (!cl.classification_id && !(cl.classification_label || '').trim()) {
          return 'Each pearl line needs a classification';
        }
      }
    }
    return null;
  }, [title, detailMode, grossWeight, goldLines, diamondLines, colorStoneLines, pearlLines, goldsmith]);

  // ---------------------------------------------------------------- submit
  const handleSubmit = async () => {
    if (validation) {
      toast.error(validation);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_id: companyId,
        goldsmith_id: goldsmith.id,
        intake_type: intakeType,
        detail_mode: detailMode,
        title: title.trim(),
        description: description.trim(),
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_address: customerAddress.trim(),
        intake_notes: intakeNotes.trim(),
        special_comments: specialComments.trim(),
        estimated_completion: estimatedCompletion || null,
        jewellery_purity_id: jewelleryPurityId || null,
        auto_create_order: true,
        incoming_gross_weight_g: grossWeight ? parseFloat(grossWeight) : null,
        gold_lines: isDetailed
          ? goldLines
              .filter((gl) => gl.purity_id && parseFloat(gl.grams) > 0)
              .map((gl) => ({ purity_id: gl.purity_id, grams: parseFloat(gl.grams) }))
          : [],
        diamond_lines: isDetailed ? normaliseCaratLines(diamondLines) : [],
        color_stone_lines: isDetailed ? normaliseCaratLines(colorStoneLines) : [],
        pearl_lines: isDetailed ? normaliseCaratLines(pearlLines) : [],
      };
      const { data } = await api.post('/repairs/items', payload);
      toast.success(`Repair item created · Order ${data?.order_no || ''}`);
      onCreated?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to create repair item'));
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------- render
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="repair-intake-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Wrench size={18} className="text-[var(--ink-navy)]" />
            <DialogTitle>Receive Jewellery for Repair</DialogTitle>
          </div>
          <DialogDescription>
            Capture a piece of jewellery being handed over to{' '}
            <span className="font-medium text-[var(--ink-navy)]">
              {goldsmith?.name || 'this goldsmith'}
            </span>{' '}
            for repair. A linked Repair Order Form (
            <span className="font-mono">RPR-…</span>) will be created automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Intake type + detail mode toggles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-[11px] text-slate-500">Intake type</Label>
            <div
              className="mt-1 inline-flex rounded-md border border-[var(--divider)] overflow-hidden text-xs"
              data-testid="intake-type-toggle"
            >
              {[
                { v: 'external', label: 'External (Customer)' },
                { v: 'internal', label: 'Internal (Our Jewellery)' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setIntakeType(opt.v)}
                  className={`px-3 py-1.5 ${
                    intakeType === opt.v
                      ? 'bg-[var(--ink-navy)] text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`intake-type-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Detail mode</Label>
            <div
              className="mt-1 inline-flex rounded-md border border-[var(--divider)] overflow-hidden text-xs"
              data-testid="detail-mode-toggle"
            >
              {[
                { v: 'gross', label: 'Gross weight only' },
                { v: 'detailed', label: 'Detailed breakdown' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setDetailMode(opt.v)}
                  className={`px-3 py-1.5 ${
                    detailMode === opt.v
                      ? 'bg-[var(--ink-navy)] text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`detail-mode-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Title + description */}
        <div className="grid grid-cols-1 gap-3 mt-3">
          <div>
            <Label className="text-[11px] text-slate-500">
              Title <span className="text-rose-600">*</span>
            </Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Customer ring — repair bent shank"
              className="h-9"
              data-testid="repair-title-input"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Short description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What needs to be repaired?"
              rows={2}
              data-testid="repair-description-input"
            />
          </div>
        </div>

        {/* External-only customer fields */}
        {isExternal && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-[11px] text-slate-500">Customer name</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g. Mrs. Roy"
                className="h-9"
                data-testid="customer-name-input"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Customer phone</Label>
              <Input
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="+91…"
                className="h-9"
                data-testid="customer-phone-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-slate-500">Customer address (optional)</Label>
              <Input
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                className="h-9"
                data-testid="customer-address-input"
              />
            </div>
          </div>
        )}

        {/* Gross weight — always shown; required in gross mode, optional in detailed */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-[11px] text-slate-500">
              Incoming gross weight (grams){' '}
              {!isDetailed && <span className="text-rose-600">*</span>}
            </Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={grossWeight}
              onChange={(e) => setGrossWeight(e.target.value)}
              placeholder="e.g. 12.345"
              className="h-9 font-mono tabular-nums"
              data-testid="gross-weight-input"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              {isDetailed
                ? 'Required for reconciliation but can match the sum of detailed lines.'
                : 'Reconciliation will match this total against the outgoing gross when received back.'}
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">
              Repair-order gold purity {!isDetailed && <span className="text-slate-400">(for RPR-order)</span>}
            </Label>
            <SearchableSelect
              items={purities || []}
              value={jewelleryPurityId}
              onValueChange={(v) => setJewelleryPurityId(v || '')}
              itemToLabel={(p) => `${p.name} (${p.percent}%)`}
              searchKeys={['name']}
              placeholder="Select purity"
              searchPlaceholder="Search purity…"
              testId="jewellery-purity-select"
            />
          </div>
        </div>

        {/* Detailed breakdown sections */}
        {isDetailed && (
          <div className="space-y-4 mt-4">
            {/* Gold lines */}
            <DetailSection
              icon={<Coins size={14} className="text-amber-600" />}
              title="Gold components"
              onAdd={addGoldLine}
              addTestId="add-gold-line"
              isEmpty={goldLines.length === 0}
              emptyHint="Add the gold purity & grams that make up the piece."
            >
              {goldLines.map((gl, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-2 items-end"
                  data-testid={`gold-line-${i}`}
                >
                  <div className="col-span-7">
                    <Label className="text-[10px] text-slate-500">Purity</Label>
                    <SearchableSelect
                      items={purities || []}
                      value={gl.purity_id}
                      onValueChange={(v) => updateGoldLine(i, { purity_id: v || '' })}
                      itemToLabel={(p) => `${p.name} (${p.percent}%)`}
                      searchKeys={['name']}
                      placeholder="Select purity"
                      searchPlaceholder="Search purity…"
                      testId={`gold-purity-select-${i}`}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[10px] text-slate-500">Grams</Label>
                    <Input
                      type="number"
                      step="0.001"
                      min="0"
                      value={gl.grams}
                      onChange={(e) => updateGoldLine(i, { grams: e.target.value })}
                      className="h-9 font-mono tabular-nums"
                      data-testid={`gold-grams-input-${i}`}
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 px-2 text-rose-600 hover:bg-rose-50"
                      onClick={() => removeGoldLine(i)}
                      data-testid={`remove-gold-line-${i}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </DetailSection>

            <CaratSection
              icon={<Gem size={14} className="text-[var(--diamond-icon)]" />}
              title="Diamond components"
              classes={diamondClass}
              lines={diamondLines}
              onChange={setDiamondLines}
              testidPrefix="diamond"
            />
            <CaratSection
              icon={<Sparkles size={14} className="text-pink-500" />}
              title="Colour stone components"
              classes={colorStoneClass}
              lines={colorStoneLines}
              onChange={setColorStoneLines}
              testidPrefix="color-stone"
            />
            <CaratSection
              icon={<Circle size={14} className="text-slate-500" />}
              title="Pearl components"
              classes={pearlClass}
              lines={pearlLines}
              onChange={setPearlLines}
              testidPrefix="pearl"
            />
          </div>
        )}

        {/* Notes */}
        <div className="grid grid-cols-1 gap-3 mt-4">
          <div>
            <Label className="text-[11px] text-slate-500">Intake notes (internal)</Label>
            <Textarea
              value={intakeNotes}
              onChange={(e) => setIntakeNotes(e.target.value)}
              placeholder="Any observations about the piece on arrival…"
              rows={2}
              data-testid="intake-notes-input"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">
              Goldsmith special instructions (bold red on PDF)
            </Label>
            <Textarea
              value={specialComments}
              onChange={(e) => setSpecialComments(e.target.value)}
              placeholder="Handle with care · Diamond is loose · Customer wants by Friday…"
              rows={2}
              data-testid="special-comments-input"
            />
          </div>
        </div>

        {validation && (
          <div
            className="mt-3 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
            data-testid="validation-error"
          >
            {validation}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            data-testid="repair-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || mastersLoading || !!validation}
            className="luxury-button"
            data-testid="repair-submit-button"
          >
            {submitting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wrench size={14} className="mr-1.5" />}
            {submitting ? 'Creating…' : 'Receive & Create Repair Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------- helpers
function normaliseCaratLines(lines) {
  return (lines || [])
    .filter((cl) => cl.classification_id || (cl.classification_label || '').trim())
    .map((cl) => ({
      classification_id: cl.classification_id || null,
      classification_label: cl.classification_label || '',
      carats: cl.carats ? parseFloat(cl.carats) : 0,
      pieces: cl.pieces ? parseInt(cl.pieces, 10) : 0,
    }));
}

function DetailSection({ icon, title, children, onAdd, addTestId, isEmpty, emptyHint }) {
  return (
    <div className="border border-[var(--divider)] rounded-md p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--ink-navy)]">
          {icon}
          {title}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={onAdd}
          data-testid={addTestId}
        >
          <Plus size={12} className="mr-1" /> Add line
        </Button>
      </div>
      {isEmpty ? (
        <div className="text-[11px] text-slate-500 italic py-1">{emptyHint}</div>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </div>
  );
}

function CaratSection({ icon, title, classes, lines, onChange, testidPrefix }) {
  const add = () => onChange((l) => [...l, { ...EMPTY_CARAT_LINE }]);
  const remove = (i) => onChange((l) => l.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <DetailSection
      icon={icon}
      title={title}
      onAdd={add}
      addTestId={`add-${testidPrefix}-line`}
      isEmpty={lines.length === 0}
      emptyHint={`Add ${title.toLowerCase()} on the piece.`}
    >
      {lines.map((cl, i) => (
        <div
          key={i}
          className="grid grid-cols-12 gap-2 items-end"
          data-testid={`${testidPrefix}-line-${i}`}
        >
          <div className="col-span-6">
            <Label className="text-[10px] text-slate-500">Classification</Label>
            {(() => {
              const items = [
                ...(classes || []).map((c) => ({ id: c.id, schema_code: c.schema_code, shape: c.shape })),
                { id: '__custom__', schema_code: 'Custom label…', shape: '' },
              ];
              return (
                <SearchableSelect
                  items={items}
                  value={cl.classification_id || '__custom__'}
                  onValueChange={(v) =>
                    update(i, {
                      classification_id: !v || v === '__custom__' ? '' : v,
                      classification_label:
                        !v || v === '__custom__' ? cl.classification_label || '' : '',
                    })
                  }
                  itemToLabel={(c) => c.schema_code}
                  itemToSubLabel={(c) => c.shape || null}
                  searchKeys={['schema_code', 'shape']}
                  placeholder="Pick a classification"
                  searchPlaceholder="Search…"
                  testId={`${testidPrefix}-class-select-${i}`}
                />
              );
            })()}
            {!cl.classification_id && (
              <Input
                value={cl.classification_label}
                onChange={(e) => update(i, { classification_label: e.target.value })}
                placeholder="Free-text label (e.g. unknown CS)"
                className="h-8 mt-1 text-xs"
                data-testid={`${testidPrefix}-label-input-${i}`}
              />
            )}
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] text-slate-500">Carats</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={cl.carats}
              onChange={(e) => update(i, { carats: e.target.value })}
              className="h-9 font-mono tabular-nums"
              data-testid={`${testidPrefix}-carats-input-${i}`}
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] text-slate-500">Pieces</Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={cl.pieces}
              onChange={(e) => update(i, { pieces: e.target.value })}
              className="h-9 font-mono tabular-nums"
              data-testid={`${testidPrefix}-pieces-input-${i}`}
            />
          </div>
          <div className="col-span-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2 text-rose-600 hover:bg-rose-50"
              onClick={() => remove(i)}
              data-testid={`${testidPrefix}-remove-line-${i}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      ))}
    </DetailSection>
  );
}

export default RepairIntakeDialog;
